import React from 'react';
import { motion } from 'framer-motion';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { useIntl } from 'react-intl';
import Gauge from '../../../../ui/canon/Gauge';
import { useCurrency } from '../../hooks/useCurrency';

const ValuationGauge = ({
  currentValue = 0,
  lowValue = 0,
  highValue = 0,
  currency,
  title = 'Your Indication of Value',
  className = '',
  // Range mode: instead of a point estimate with a ±band, present a floor→ceiling
  // range. The headline shows "low – high", the needle pins to the ceiling (high),
  // and the corner sublabels are caller-supplied (e.g. "1× Revenue" / "DCF").
  rangeMode = false,
  lowSublabel = null,
  highSublabel = null,
}) => {
  const intl = useIntl();
  const { symbol: defaultSymbol } = useCurrency();
  const resolvedCurrency = currency || defaultSymbol;
  const formatValue = (v) => {
    const n = Number(v || 0);
    if (n >= 1_000_000) return `${resolvedCurrency}${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${resolvedCurrency}${(n / 1_000).toFixed(1)}K`;
    return `${resolvedCurrency}${n.toFixed(0)}`;
  };
  // In range mode the ceiling (high) is the anchor the needle sits on, so the gauge
  // reads as "value spans up to here", not "value sits in the middle of a band".
  const gaugeValue = rangeMode ? highValue : currentValue;
  const resolvedLowSublabel = lowSublabel ?? intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_conservative' });
  const resolvedHighSublabel = highSublabel ?? intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_optimistic' });

  return (
    <div className={`w-full ${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-succession-text-secondary mb-3">{title}</h2>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className={`${rangeMode ? 'text-5xl' : 'text-6xl'} font-bold text-succession-text-primary`}>
          {rangeMode ? `${formatValue(lowValue)} – ${formatValue(highValue)}` : formatValue(currentValue)}
        </motion.div>
      </div>

      <Gauge currentValue={gaugeValue} lowValue={lowValue} highValue={highValue} format={formatValue} needleOffsetPx={-6} />

      <div className="flex justify-between items-start px-2 mt-8">
        <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.5 }} className="flex flex-col items-start">
          <div className="flex items-center gap-2 mb-1">
            <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_low' })}</span>
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">{formatValue(lowValue)}</div>
          <div className="flex items-center gap-1 text-sm text-succession-text-secondary italic mt-1"><span>◄</span><span>{resolvedLowSublabel}</span></div>
        </motion.div>
        <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.5 }} className="flex flex-col items-end">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.gauge_high' })}</span>
            <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-succession-text-primary">{formatValue(highValue)}</div>
          <div className="flex items-center gap-1 text-sm text-succession-text-secondary italic mt-1"><span>{resolvedHighSublabel}</span><span>►</span></div>
        </motion.div>
      </div>
    </div>
  );
};

export default ValuationGauge;


